import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useAccessRestrictions } from 'app/frontend/base/config/queries'
import { z } from 'zod'
import AccessRestrictionTable from '../AccessSectionTable'
import { useQueryClient } from '@tanstack/react-query'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import { updateGuild } from 'app/frontend/base/api/guild'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { zSetting } from 'app/frontend/base/helpers/forms'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import { FormGroup } from '@/components/ui/form'
import { useRefreshData } from 'app/frontend/base/hooks/useRefreshData'
import {
  accessRestrictionSchema,
  EditableAccessRestriction,
  validateAccessRestrictions,
} from '../accessRestrictionSchema'

const formSchema = z
  .object({
    settings: z.object({
      wishlistOverviewVisibility: zSetting(
        z.enum(['entire_team', 'admins_only']),
      ),
      wishlistLootHistoryVisibility: zSetting(
        z.enum(['entire_team', 'admins_only']),
      ),
    }),
    access_restrictions_attributes: z.array(accessRestrictionSchema),
  })
  .superRefine(validateAccessRestrictions)

const AccessSection = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { team, guild } = useTeam()
  const { data, isPending } = useAccessRestrictions(team?.id)

  const { refreshing, refreshData } = useRefreshData({
    retriever: updateGuild,
    id: guild?.id,
    successKey: 'settings.access.refresh-cache-success',
    queryToInvalidate: ['guild', { path: guild?.path }],
  })

  if (!team || !data || isPending) return null

  return (
    <div>
      <h4>Access rules</h4>

      <p className='mt-6'>
        You can grant access to your team with rules specified here. Users will
        have access according to the matching rule of the highest level.{' '}
        <em>
          The in-game guild master will always have admin access, regardless of
          rules.
        </em>
      </p>

      <p className='mt-2 mb-8'>
        Sections that you have no (or only user specific) rules for will use the
        base access rules. However, users without base access will not be able
        to see any sections, regardless of any section rules. Users with base
        level admin access will always have admin access to all sections.
      </p>

      <FormWrapper<
        wowaudit.Team & {
          access_restrictions_attributes: EditableAccessRestriction[]
        }
      >
        key={team?.id}
        formSchema={formSchema}
        entity={{
          ...team,
          access_restrictions_attributes: data ?? [],
        }}
        entityType='team'
        extraActions={[
          <Button
            key='refresh'
            variant='outline'
            size='sm'
            loading={refreshing}
            spinner='blizzard'
            className='flex items-center gap-3 min-w-28'
            disabled={!guild?.id}
            onClick={() => refreshData()}
          >
            {t('settings.access.refresh-cache')}
          </Button>,
        ]}
        onSave={async (form, data) => {
          const newRestrictions = await queryClient.fetchQuery({
            queryKey: ['accessRestrictions', { id: team?.id }],
          })

          form.reset({
            ...data,
            access_restrictions_attributes: newRestrictions ?? [],
          })
        }}
      >
        <h5>Team rules</h5>
        <AccessRestrictionTable scope='team' />

        <ProtectedContent
          requirement='admin'
          section='base'
          alwaysRenderContent={true}
          requireAllTeams={true}
        >
          <h5>Guild rules</h5>
          <AccessRestrictionTable scope='owner' />
        </ProtectedContent>

        <h5 className='mt-6'>Page access</h5>

        <p className='mb-6'>
          In addition to section rules, you can restrict access to some specific
          pages here. Users must still have access to the section to see these
          pages.
        </p>

        <FormGroup className='*:space-y-2 *:grow'>
          <SettingField name='wishlistOverviewVisibility' layout='inline' />
          <SettingField name='wishlistLootHistoryVisibility' layout='inline' />
        </FormGroup>
      </FormWrapper>
    </div>
  )
}

export default AccessSection
